import { Filter, FilterGroup } from 'conversations/components/FilterComponents/CompoundFilter/types'
import { MS_IN_SECOND } from 'conversations/utils/timeConstants'
import { intersection } from 'ramda'
import Conversation from 'shared/typings/conversations/Conversation'
import {
  ConversationSidebarFilterCondition,
  ConversationSidebarFilterLogicalCondition,
  ConversationSidebarFilterValueCondition,
  ConversationSidebarNumericFilter,
  NumericOperators,
} from 'shared/typings/conversationsSidebar/Filters'
import { Team } from 'shared/typings/teams/Team'

export const HARDCODED_TEAMMATE_IDS = {
  NO_PARTICIPANTS: 0,
  ALL_PARTICIPANTS: -1,
}

export const QUEUE_TIME_PROPERTY = 'unresponded-time-since-routed'

const NO_PARTICIPANTS_CONDITION: ConversationSidebarFilterCondition = {
  operator: 'empty',
  condition: {
    type: 'COLLECTION',
    source: 'CONVERSATION',
    name: 'participants',
    value: [],
    contentType: 'NUMERIC',
  },
}

const ALL_PARTICIPANTS_CONDITION: ConversationSidebarFilterCondition = {
  operator: 'not_empty',
  condition: {
    type: 'COLLECTION',
    source: 'CONVERSATION',
    name: 'participants',
    value: [],
    contentType: 'NUMERIC',
  },
}

const anyMatchValueCondition = (
  propertyName: string,
  collection: string[] | number[]
): ConversationSidebarFilterCondition => ({
  operator: 'any_match',
  condition: {
    type: 'COLLECTION',
    source: 'CONVERSATION',
    name: propertyName,
    value: collection,
    contentType: 'NUMERIC',
  },
})

const numericValueCondition = (
  propertyName: string,
  operator: string,
  value: number
): ConversationSidebarFilterCondition => ({
  operator: NumericOperators[operator],
  condition: {
    type: 'NUMERIC',
    source: 'CONVERSATION',
    name: propertyName,
    value,
    contentType: 'NUMERIC',
  },
})

const isValueCondition = (condition: ConversationSidebarFilterCondition) => {
  return !['or', 'and', 'not'].includes(condition.operator)
}

export const filtersToSubscriptionCondition = (
  filterGroup: FilterGroup
): ConversationSidebarFilterCondition => {
  const conditions: ConversationSidebarFilterCondition[] = []
  filterGroup.forEach((filterSet) => {
    if (filterSet.length === 1) {
      conditions.push(filterToCondition(filterSet[0]))
    } else {
      conditions.push({
        operator: 'and',
        condition: filterSet.map((filter) => filterToCondition(filter)),
      })
    }
  })

  if (conditions.length === 1) {
    return conditions[0]
  }

  return {
    operator: 'or',
    condition: conditions,
  }
}

export const filterToCondition = (filter: Filter): ConversationSidebarFilterCondition => {
  if (filter.property === 'participants' && filter.values.some((value) => value <= 0)) {
    if (filter.values.some((value) => Number(value) === HARDCODED_TEAMMATE_IDS.ALL_PARTICIPANTS)) {
      // fixme a section with all participants OR no participants should catch all convos, but wont catch ones w/o participants right now
      //  there is already an All Conversations section for this use case
      return ALL_PARTICIPANTS_CONDITION
    }
    return NO_PARTICIPANTS_CONDITION
  } else if (filter.property === QUEUE_TIME_PROPERTY) {
    return numericValueCondition(filter.property, filter.operator, parseInt(filter.values[0]))
  } else {
    return anyMatchValueCondition(
      filter.property,
      filter.values.map((value) => Number(value))
    )
  }
}

export const createConditionsArray = (sectionCondition: ConversationSidebarFilterCondition) => {
  if (Array.isArray(sectionCondition.condition)) {
    return sectionCondition.condition
  }
  return [{ ...sectionCondition }]
}

export const isQueueTimeSection = (conditions: ConversationSidebarFilterCondition[]) => {
  let isQueueTimeOnlySection = false
  if (conditions) {
    isQueueTimeOnlySection = hasQueueTimeCondition(conditions) && conditions.length === 1
  }
  return isQueueTimeOnlySection
}

export const subscriptionConditionToFilterGroup = (
  condition: ConversationSidebarFilterCondition
): FilterGroup => {
  const filterGroup: FilterGroup = []
  const filters: Filter[] = []
  if (isValueCondition(condition)) {
    filters.push(conditionToFilter(condition))
    filterGroup.push(filters)
  } else {
    const logicalCondition = condition as ConversationSidebarFilterLogicalCondition
    const logicalOperator = logicalCondition.operator

    switch (logicalOperator) {
      case 'and': {
        if (Array.isArray(logicalCondition.condition)) {
          logicalCondition.condition.forEach((c) => filters.push(conditionToFilter(c)))
          filterGroup.push(filters)
        }
        break
      }
      case 'or': {
        if (Array.isArray(logicalCondition.condition)) {
          logicalCondition.condition.forEach((c) => {
            const group = subscriptionConditionToFilterGroup(c)
            filterGroup.push(group[0])
          })
        }
        break
      }
      default: {
        break
      }
    }
  }
  return filterGroup
}

export const conditionToFilter = (condition: ConversationSidebarFilterCondition): Filter => {
  if (isValueCondition(condition)) {
    const valueCondition = (condition as ConversationSidebarFilterValueCondition).condition
    const valueOperator = (condition as ConversationSidebarFilterValueCondition).operator
    // assumption that the only supported empty condition is no participants
    if (valueOperator === 'empty') {
      return {
        property: valueCondition.name,
        operator: 'EQ',
        values: [`${HARDCODED_TEAMMATE_IDS.NO_PARTICIPANTS}`],
      }
    }
    if (valueOperator === 'not_empty') {
      return {
        property: valueCondition.name,
        operator: 'EQ',
        values: [`${HARDCODED_TEAMMATE_IDS.ALL_PARTICIPANTS}`],
      }
    }
    const findNumericOperator = Object.entries(NumericOperators).find(
      ([, val]) => valueOperator === val
    )
    const isSingleValue = !Array.isArray(valueCondition.value)
    const valuesArray = isSingleValue
      ? valueCondition.value
        ? [valueCondition.value.toString()]
        : []
      : (valueCondition.value as number[]).map((value) => value.toString())
    return {
      property: valueCondition.name,
      operator: findNumericOperator ? findNumericOperator[0] : 'EQ',
      values: valuesArray,
    }
  } else {
    /* TODO this ELSE is only here to support previous no participant conditions.
        In an ideal world we can patch this condition to the single "empty" filter instead
        of the ORed conditions.
    */
    const logicalCondition = condition as ConversationSidebarFilterLogicalCondition
    // we only support logical conditions for all participants and no participants
    // so, we can take significant shortcuts here
    // a fully-realized version of this translation would be very hard
    const noParticipantsConditions =
      logicalCondition.condition as ConversationSidebarFilterValueCondition[]
    return {
      property: noParticipantsConditions[0].condition.name,
      operator: 'EQ',
      values: [
        `${HARDCODED_TEAMMATE_IDS.NO_PARTICIPANTS}`,
        ...noParticipantsConditions.flatMap(({ condition }) =>
          (condition.value as number[]).map((value) => value.toString())
        ),
      ],
    }
  }
}

export const hasQueueTimeCondition = (conditions: ConversationSidebarFilterCondition[]) =>
  !!conditions.find((c) => {
    const cond = c.condition as ConversationSidebarNumericFilter
    return cond.name === QUEUE_TIME_PROPERTY
  })

export const evaluateCondition = (
  conversation: Conversation,
  teams: Record<number, Team>,
  condition: ConversationSidebarFilterCondition
): boolean => {
  if (isValueCondition(condition)) {
    // value conditions are e.g. tag in [ tag1, tag2 ]
    const valueCondition = (condition as ConversationSidebarFilterValueCondition).condition
    const valueOperator = (condition as ConversationSidebarFilterValueCondition).operator
    switch (valueCondition.name) {
      case QUEUE_TIME_PROPERTY: {
        const props = conversation.conversationProperties
        const numericCondition = condition.condition as ConversationSidebarNumericFilter
        if (numericCondition.value && props && props.lastRoutedTime) {
          return (
            props.lastAgentResponseTime === null &&
            props.lastRoutedTime < Date.now() - numericCondition.value * MS_IN_SECOND
          )
        }
        return false
      }
      case 'tags':
        return intersection(conversation.tags || [], valueCondition.value as number[]).length > 0
      case 'team':
        return conversation.participantIds.some((participant: number) =>
          (valueCondition.value as number[]).find((team) =>
            teams[team]?.members.includes(participant)
          )
        )
      case 'participants':
        if (valueOperator === 'empty') {
          // no participants
          return conversation.participantIds.length === 0
        } else if (valueOperator === 'not_empty') {
          // all participants
          return conversation.participantIds.length > 0
        } else {
          return conversation.participantIds.some((participant: number) =>
            (valueCondition.value as number[]).find(
              (desiredParticipant) => participant === desiredParticipant
            )
          )
        }
      default:
        return false
    }
  } else {
    // logical conditions are e.g. condition A and condition B
    const logicalCondition = condition as ConversationSidebarFilterLogicalCondition
    const combinationConditions = logicalCondition.condition as ConversationSidebarFilterCondition[]
    const negationCondition = logicalCondition.condition as ConversationSidebarFilterCondition
    switch (logicalCondition.operator) {
      case 'and':
        return combinationConditions.every((child) => evaluateCondition(conversation, teams, child))
      case 'or':
        return combinationConditions.some((child) => evaluateCondition(conversation, teams, child))
      case 'not':
        return !evaluateCondition(conversation, teams, negationCondition)
    }
  }
}

export const getAllValuesOfType = (
  type: string,
  condition: ConversationSidebarFilterCondition
): any[] => {
  if (isValueCondition(condition)) {
    const valueCondition = (condition as ConversationSidebarFilterValueCondition).condition
    const valueOperator = (condition as ConversationSidebarFilterValueCondition).operator
    if (valueOperator === 'empty') {
      return valueCondition.name === type ? [HARDCODED_TEAMMATE_IDS.NO_PARTICIPANTS] : []
    } else if (valueOperator === 'not_empty') {
      return valueCondition.name === type ? [HARDCODED_TEAMMATE_IDS.ALL_PARTICIPANTS] : []
    } else if (valueCondition.value) {
      return valueCondition.name === type ? valueCondition.value : []
    } else {
      return []
    }
  } else {
    const logicalCondition = condition as ConversationSidebarFilterLogicalCondition
    const combinationConditions = logicalCondition.condition as ConversationSidebarFilterCondition[]
    const negationCondition = logicalCondition.condition as ConversationSidebarFilterCondition
    switch (logicalCondition.operator) {
      case 'and':
      case 'or':
        return combinationConditions.flatMap((child) => getAllValuesOfType(type, child))
      case 'not':
        return getAllValuesOfType(type, negationCondition)
    }
  }
}
